import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { notesAPI } from '../services/api';
import { jsPDF } from 'jspdf';
import './Note.css';

const Note = () => {
  const { noteId } = useParams();
  const navigate = useNavigate();
  const [noteName, setNoteName] = useState('');
  const [content, setContent] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [pageSettings, setPageSettings] = useState({
    marginTop: 20,
    marginBottom: 20,
    marginLeft: 20,
    marginRight: 20,
    pageWidth: 210, // A4 width in mm
    pageHeight: 297, // A4 height in mm
    fontSize: 12,
    fontFamily: 'Arial',
    lineHeight: 1.6
  });
  const saveTimeoutRef = useRef(null);
  const nameInputRef = useRef(null);
  
  // Undo/Redo history - completely recoded for reliability
  const historyRef = useRef([]);
  const historyIndexRef = useRef(-1);
  const maxHistorySize = 100;
  const isUndoRedoRef = useRef(false);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const contentChangeTimeoutRef = useRef(null);
  const lastSavedContentRef = useRef('');
  
  // Helper function to save state to history
  const saveToHistory = useCallback((currentContent) => {
    // Don't save if we're in the middle of undo/redo
    if (isUndoRedoRef.current) {
      return;
    }
    
    // Don't save if content hasn't changed
    if (currentContent === lastSavedContentRef.current) {
      return;
    }
    
    const history = historyRef.current;
    const currentIndex = historyIndexRef.current;
    
    // Remove any history after current index (if user made a new change after undo)
    if (currentIndex < history.length - 1) {
      history.splice(currentIndex + 1);
    }
    
    // Add new state to history
    history.push(currentContent);
    lastSavedContentRef.current = currentContent;
    
    // Limit history size
    if (history.length > maxHistorySize) {
      history.shift();
    } else {
      historyIndexRef.current = history.length - 1;
    }
    
    // Update undo/redo button states
    setCanUndo(historyIndexRef.current > 0);
    setCanRedo(false);
  }, []);

  // Undo function
  const undo = useCallback(() => {
    const history = historyRef.current;
    if (historyIndexRef.current <= 0 || history.length === 0) {
      return;
    }
    
    isUndoRedoRef.current = true;
    historyIndexRef.current--;
    const previousContent = history[historyIndexRef.current];
    
    if (previousContent !== undefined) {
      setContent(previousContent);
      lastSavedContentRef.current = previousContent;
    }
    
    setCanUndo(historyIndexRef.current > 0);
    setCanRedo(historyIndexRef.current < history.length - 1);
    
    setTimeout(() => {
      isUndoRedoRef.current = false;
    }, 100);
  }, []);

  // Redo function
  const redo = useCallback(() => {
    const history = historyRef.current;
    if (historyIndexRef.current >= history.length - 1 || history.length === 0) {
      return;
    }
    
    isUndoRedoRef.current = true;
    historyIndexRef.current++;
    const nextContent = history[historyIndexRef.current];
    
    if (nextContent !== undefined) {
      setContent(nextContent);
      lastSavedContentRef.current = nextContent;
    }
    
    setCanUndo(historyIndexRef.current > 0);
    setCanRedo(historyIndexRef.current < history.length - 1);
    
    setTimeout(() => {
      isUndoRedoRef.current = false;
    }, 100);
  }, []);

  // Handle keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't handle shortcuts if user is editing note name or in an input field
      if (isEditingName || e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }
      
      // Ctrl+Z or Cmd+Z for undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      }
      // Ctrl+Y or Cmd+Shift+Z for redo
      else if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        redo();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [undo, redo, isEditingName]);

  // Load note data
  useEffect(() => {
    const loadNote = async () => {
      try {
        setIsLoading(true);
        const note = await notesAPI.getById(noteId);
        const loadedContent = note.content || '';
        setNoteName(note.noteName || 'Untitled Note');
        setContent(loadedContent);
        
        // Load page settings if saved
        if (note.pageSettings) {
          setPageSettings(note.pageSettings);
        }
        
        // Initialize history with loaded content
        historyRef.current = [loadedContent];
        historyIndexRef.current = 0;
        lastSavedContentRef.current = loadedContent;
        setCanUndo(false);
        setCanRedo(false);
      } catch (error) {
        console.error('Error loading note:', error);
        setNoteName('Untitled Note');
        setContent('');
        historyRef.current = [''];
        historyIndexRef.current = 0;
        lastSavedContentRef.current = '';
        setCanUndo(false);
        setCanRedo(false);
      } finally {
        setIsLoading(false);
      }
    };

    if (noteId) {
      loadNote();
    }
  }, [noteId]);

  // Save to history when content changes (debounced - save after user stops typing)
  useEffect(() => {
    if (isLoading || isUndoRedoRef.current) return;
    
    // Clear any existing timeout
    if (contentChangeTimeoutRef.current) {
      clearTimeout(contentChangeTimeoutRef.current);
    }
    
    // Save to history after user stops typing for 1.5 seconds
    // This captures meaningful changes (words, sentences) rather than individual keystrokes
    contentChangeTimeoutRef.current = setTimeout(() => {
      if (!isUndoRedoRef.current && content !== undefined) {
        saveToHistory(content);
      }
    }, 1500);
    
    return () => {
      if (contentChangeTimeoutRef.current) {
        clearTimeout(contentChangeTimeoutRef.current);
      }
    };
  }, [content, isLoading, saveToHistory]);

  // Auto-save content after user stops typing
  useEffect(() => {
    if (!noteId || isLoading || isEditingName) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      if (noteId && noteName.trim()) {
        handleSave();
      }
    }, 1000);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, noteName, pageSettings]);

  const handleSave = async () => {
    if (!noteId || !noteName.trim()) return;

    try {
      setIsSaving(true);
      await notesAPI.update(noteId, noteName.trim(), content, pageSettings);
    } catch (error) {
      console.error('Error saving note:', error);
      alert('Failed to save note. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleNameDoubleClick = () => {
    setIsEditingName(true);
  };

  const handleNameBlur = () => {
    setIsEditingName(false);
    if (noteName.trim() && noteId) {
      handleSave();
    }
  };

  const handleNameKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setIsEditingName(false);
      if (noteName.trim() && noteId) {
        handleSave();
      }
    } else if (e.key === 'Escape') {
      setIsEditingName(false);
    }
  };

  const handleDeleteNote = async () => {
    if (!window.confirm('Are you sure you want to delete this note? This action cannot be undone.')) {
      return;
    }

    try {
      await notesAPI.delete(noteId);
      navigate('/dashboard');
    } catch (error) {
      console.error('Error deleting note:', error);
      alert('Failed to delete note. Please try again.');
    }
  };

  // Export as PDF
  const handleExportPDF = async () => {
    try {
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // Set margins
      const marginLeft = pageSettings.marginLeft;
      const marginTop = pageSettings.marginTop;
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const contentWidth = pageWidth - marginLeft - pageSettings.marginRight;

      // Set font
      doc.setFont(pageSettings.fontFamily);
      doc.setFontSize(pageSettings.fontSize);

      // Create a temporary div to parse HTML and extract text
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = content;
      
      // Extract text preserving some structure
      const text = tempDiv.innerText || tempDiv.textContent || '';
      const lines = doc.splitTextToSize(text, contentWidth);
      
      let y = marginTop;
      const lineHeight = pageSettings.fontSize * pageSettings.lineHeight * 0.35; // Convert to mm
      
      lines.forEach((line) => {
        if (y + lineHeight > pageHeight - pageSettings.marginBottom) {
          doc.addPage();
          y = marginTop;
        }
        doc.text(line, marginLeft, y);
        y += lineHeight;
      });

      doc.save(`${noteName || 'note'}.pdf`);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Failed to export as PDF. Please try again.');
    }
  };

  // Export as HTML
  const handleExportHTML = () => {
    const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${noteName || 'Note'}</title>
  <style>
    body {
      font-family: ${pageSettings.fontFamily};
      font-size: ${pageSettings.fontSize}pt;
      line-height: ${pageSettings.lineHeight};
      margin: ${pageSettings.marginTop}mm ${pageSettings.marginRight}mm ${pageSettings.marginBottom}mm ${pageSettings.marginLeft}mm;
      padding: 0;
    }
  </style>
</head>
<body>
  ${content}
</body>
</html>`;
    
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${noteName || 'note'}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Export as DOCX (using HTML to DOCX conversion)
  const handleExportDOCX = async () => {
    try {
      // Create HTML content with styles
      const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${noteName || 'Note'}</title>
  <style>
    body {
      font-family: ${pageSettings.fontFamily};
      font-size: ${pageSettings.fontSize}pt;
      line-height: ${pageSettings.lineHeight};
      margin: ${pageSettings.marginTop}mm ${pageSettings.marginRight}mm ${pageSettings.marginBottom}mm ${pageSettings.marginLeft}mm;
      padding: 0;
    }
  </style>
</head>
<body>
  ${content}
</body>
</html>`;

      // Convert HTML to Blob
      const blob = new Blob(['\ufeff', htmlContent], { type: 'application/msword' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${noteName || 'note'}.doc`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting DOCX:', error);
      alert('Failed to export as DOCX. Please try exporting as HTML or PDF instead.');
    }
  };

  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingName]);

  // Add tooltips to Quill toolbar buttons
  useEffect(() => {
    if (isLoading) return;
    
    const addTooltips = () => {
      const toolbar = document.querySelector('.note-content-wrapper .ql-toolbar');
      if (!toolbar) return;

      // Tooltip mappings by class name
      const tooltipMap = {
        'ql-bold': 'Bold (Ctrl+B)',
        'ql-italic': 'Italic (Ctrl+I)',
        'ql-underline': 'Underline (Ctrl+U)',
        'ql-strike': 'Strikethrough',
        'ql-link': 'Insert Link',
        'ql-image': 'Insert Image',
        'ql-clean': 'Remove Formatting',
        'ql-blockquote': 'Blockquote',
        'ql-code-block': 'Code Block',
        'ql-header': 'Heading Style',
        'ql-font': 'Font Family',
        'ql-size': 'Font Size',
        'ql-color': 'Text Color',
        'ql-background': 'Background Color',
        'ql-align': 'Text Alignment',
        'ql-script': 'Subscript/Superscript',
        'ql-indent': 'Indentation',
        'ql-list': 'List'
      };

      // Add tooltips to all buttons
      Object.keys(tooltipMap).forEach(className => {
        const buttons = toolbar.querySelectorAll(`.${className}`);
        buttons.forEach(button => {
          if (!button.getAttribute('title')) {
            // Check for specific values
            const value = button.getAttribute('value');
            if (className === 'ql-list' && value === 'ordered') {
              button.setAttribute('title', 'Numbered List');
            } else if (className === 'ql-list' && value === 'bullet') {
              button.setAttribute('title', 'Bullet List');
            } else if (className === 'ql-indent' && value === '-1') {
              button.setAttribute('title', 'Decrease Indent');
            } else if (className === 'ql-indent' && value === '+1') {
              button.setAttribute('title', 'Increase Indent');
            } else if (className === 'ql-script' && value === 'sub') {
              button.setAttribute('title', 'Subscript');
            } else if (className === 'ql-script' && value === 'super') {
              button.setAttribute('title', 'Superscript');
            } else {
              button.setAttribute('title', tooltipMap[className]);
            }
          }
        });
      });

      // Add tooltips to picker labels
      const pickerLabels = toolbar.querySelectorAll('.ql-picker-label');
      pickerLabels.forEach(label => {
        const picker = label.closest('.ql-picker');
        if (picker) {
          const pickerClass = Array.from(picker.classList).find(cls => cls.startsWith('ql-') && cls !== 'ql-picker');
          if (pickerClass && tooltipMap[pickerClass] && !label.getAttribute('title')) {
            label.setAttribute('title', tooltipMap[pickerClass]);
          }
        }
      });
    };

    // Wait for Quill to render, then add tooltips
    const timeout = setTimeout(addTooltips, 300);
    
    return () => {
      clearTimeout(timeout);
    };
  }, [isLoading]);

  // Quill modules configuration - memoized to prevent re-renders
  const modules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'font': [] }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'script': 'sub'}, { 'script': 'super' }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'align': [] }],
      ['blockquote', 'code-block'],
      ['link', 'image'],
      ['clean']
    ],
  }), []);

  const formats = useMemo(() => [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'script',
    'list', 'bullet', 'indent',
    'align',
    'blockquote', 'code-block',
    'link', 'image'
  ], []);

  if (isLoading) {
    return (
      <div className="note-page">
        <div className="note-loading">Loading note...</div>
      </div>
    );
  }

  return (
    <div className="note-page">
      <div className="note-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
          {isEditingName ? (
            <input
              ref={nameInputRef}
              type="text"
              value={noteName}
              onChange={(e) => setNoteName(e.target.value)}
              onBlur={handleNameBlur}
              onKeyDown={handleNameKeyDown}
              className="note-name-input"
              maxLength={100}
              style={{ flex: 1 }}
            />
          ) : (
            <h1 
              onDoubleClick={handleNameDoubleClick} 
              className="note-name-editable" 
              title="Double-click to edit" 
              style={{ flex: 1, margin: 0 }}
            >
              {noteName || 'Untitled Note'}
              <span className="edit-icon">✎</span>
            </h1>
          )}
          {isSaving && (
            <span className="note-saving-indicator" title="Saving...">
              Saving...
            </span>
          )}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              onClick={undo}
              disabled={!canUndo}
              className="note-toolbar-btn"
              title="Undo (Ctrl+Z / Cmd+Z)"
            >
              ↶ Undo
            </button>
            <button
              onClick={redo}
              disabled={!canRedo}
              className="note-toolbar-btn"
              title="Redo (Ctrl+Y / Cmd+Shift+Z)"
            >
              ↷ Redo
            </button>
            <div style={{ width: '1px', height: '24px', backgroundColor: '#e5e7eb', margin: '0 4px' }}></div>
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="note-toolbar-btn"
              title="Page Settings"
            >
              ⚙️ Settings
            </button>
            <div className="note-export-dropdown" style={{ position: 'relative' }}>
              <button
                className="note-toolbar-btn"
                title="Export Note"
                onClick={() => setShowExportMenu(!showExportMenu)}
              >
                📄 Export
              </button>
              {showExportMenu && (
                <div 
                  className="note-export-menu"
                  onMouseLeave={() => setShowExportMenu(false)}
                >
                  <button onClick={() => { handleExportPDF(); setShowExportMenu(false); }}>Export as PDF</button>
                  <button onClick={() => { handleExportHTML(); setShowExportMenu(false); }}>Export as HTML</button>
                  <button onClick={() => { handleExportDOCX(); setShowExportMenu(false); }}>Export as DOCX</button>
                </div>
              )}
            </div>
          <button
            onClick={handleDeleteNote}
            className="delete-note-header-btn"
            title="Delete this note"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 6H5H21M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M10 11V17M14 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
      </div>

      {/* Page Settings Panel */}
      {showSettings && (
        <div className="note-settings-panel">
          <h3 style={{ marginBottom: '16px', fontSize: '1rem', fontWeight: '600' }}>Page Settings</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.875rem', fontWeight: '500' }}>Margin Top (mm)</label>
              <input
                type="number"
                value={pageSettings.marginTop}
                onChange={(e) => setPageSettings({ ...pageSettings, marginTop: parseFloat(e.target.value) || 0 })}
                className="note-settings-input"
                min="0"
                step="1"
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.875rem', fontWeight: '500' }}>Margin Bottom (mm)</label>
              <input
                type="number"
                value={pageSettings.marginBottom}
                onChange={(e) => setPageSettings({ ...pageSettings, marginBottom: parseFloat(e.target.value) || 0 })}
                className="note-settings-input"
                min="0"
                step="1"
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.875rem', fontWeight: '500' }}>Margin Left (mm)</label>
              <input
                type="number"
                value={pageSettings.marginLeft}
                onChange={(e) => setPageSettings({ ...pageSettings, marginLeft: parseFloat(e.target.value) || 0 })}
                className="note-settings-input"
                min="0"
                step="1"
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.875rem', fontWeight: '500' }}>Margin Right (mm)</label>
              <input
                type="number"
                value={pageSettings.marginRight}
                onChange={(e) => setPageSettings({ ...pageSettings, marginRight: parseFloat(e.target.value) || 0 })}
                className="note-settings-input"
                min="0"
                step="1"
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.875rem', fontWeight: '500' }}>Font Family</label>
              <select
                value={pageSettings.fontFamily}
                onChange={(e) => setPageSettings({ ...pageSettings, fontFamily: e.target.value })}
                className="note-settings-input"
              >
                <option value="Arial">Arial</option>
                <option value="Times New Roman">Times New Roman</option>
                <option value="Courier New">Courier New</option>
                <option value="Georgia">Georgia</option>
                <option value="Verdana">Verdana</option>
                <option value="Helvetica">Helvetica</option>
                <option value="Calibri">Calibri</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.875rem', fontWeight: '500' }}>Font Size (pt)</label>
              <input
                type="number"
                value={pageSettings.fontSize}
                onChange={(e) => setPageSettings({ ...pageSettings, fontSize: parseFloat(e.target.value) || 12 })}
                className="note-settings-input"
                min="8"
                max="72"
                step="1"
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.875rem', fontWeight: '500' }}>Line Height</label>
              <input
                type="number"
                value={pageSettings.lineHeight}
                onChange={(e) => setPageSettings({ ...pageSettings, lineHeight: parseFloat(e.target.value) || 1.6 })}
                className="note-settings-input"
                min="1"
                max="3"
                step="0.1"
              />
            </div>
          </div>
          <button
            onClick={() => setShowSettings(false)}
            className="note-settings-close-btn"
          >
            Close Settings
          </button>
        </div>
      )}

      <div className="note-content-wrapper" style={{
        marginTop: `${pageSettings.marginTop}px`,
        marginBottom: `${pageSettings.marginBottom}px`,
        marginLeft: `${pageSettings.marginLeft}px`,
        marginRight: `${pageSettings.marginRight}px`,
        fontFamily: pageSettings.fontFamily,
        fontSize: `${pageSettings.fontSize}pt`,
        lineHeight: pageSettings.lineHeight
      }}>
        <ReactQuill
          theme="snow"
          value={content}
          onChange={(value) => {
            setContent(value);
          }}
          onBlur={() => {
            // Save to history when editor loses focus (user clicks away)
            if (!isUndoRedoRef.current && content !== undefined) {
              saveToHistory(content);
            }
          }}
          modules={modules}
          formats={formats}
          placeholder="Start typing your note here..."
          style={{
            height: 'calc(100vh - 200px)',
            display: 'flex',
            flexDirection: 'column'
          }}
        />
      </div>
    </div>
  );
};

export default Note;
